import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';
import ConsumoPage from './pages/ConsumoPage';
import YiqiImportPage from './pages/YiqiImportPage';
import AdminChatTestPage from './pages/AdminChatTestPage';
import AdminUsersPage from './pages/AdminUsersPage';
import OrdenCompraPage from './pages/OrdenCompraPage';
import AprobacionCotizacionesPage from './pages/AprobacionCotizacionesPage';
import FacturasServiciosPage from './pages/FacturasServiciosPage';
import ProformasPage from './pages/ProformasPage';
import TareasProgramadasPage from './pages/TareasProgramadasPage';
// Recibos de Cobro y Retenciones de Clientes ya no se rutean por separado:
// viven como pestañas dentro de CobranzasPage.
import CobranzasPage from './pages/CobranzasPage';
import LoadDocsPage from './pages/LoadDocsPage';
import PanelesPage from './pages/PanelesPage';
import PanelDiarioPage from './pages/PanelDiarioPage';
import ConciliacionArcaPage from './pages/ConciliacionArcaPage';
import AdminLayout from './components/AdminLayout';
import { Loader2 } from 'lucide-react';

// Ruta de aterreo dentro de /admin. El super admin cae en una herramienta por
// defecto (ve todo el menú igual); el usuario con alcance, en su primera
// herramienta asignada. No depende de un catálogo estático.
const DEFAULT_ADMIN_PATH = '/admin/consumo';
const firstAllowedAdminPath = (isAdmin, adminTools) => {
  if (isAdmin) return DEFAULT_ADMIN_PATH;
  if (adminTools && adminTools.length > 0) return `/admin/${adminTools[0]}`;
  return '/';
};

const Cargando = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-muted-foreground">Cargando...</p>
    </div>
  </div>
);

// Resolvedor de la raíz. `/` ya no es el chat: acá se decide a dónde va cada
// usuario según cuántos paneles tenga disponibles.
//   - loader            → su única pantalla
//   - exactamente 1     → entra directo, sin pantalla intermedia
//   - 2 o más           → selector de paneles
//   - ninguno           → el selector muestra su estado vacío
const IndexResolver = () => {
  const { isAuthenticated, isLoader, paneles, loading } = useAuth();

  if (loading) return <Cargando />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isLoader) return <Navigate to="/load-docs" replace />;
  if (paneles.length === 1) return <Navigate to={paneles[0].path} replace />;

  return <PanelesPage />;
};

// Ruta del panel de quickactions (/diagnostico)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoader, canAccessChat, loading } = useAuth();

  if (loading) return <Cargando />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Los usuarios "loader" solo pueden usar /load-docs (ni quickactions ni admin)
  if (isLoader) {
    return <Navigate to="/load-docs" replace />;
  }

  // Sin áreas asignadas no hay quickactions que mostrar: que lo resuelva la raíz.
  if (!canAccessChat) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Ruta standalone de /load-docs (sin menú) — la usan los "loader". Admins y
// users con la herramienta asignada la ven dentro del panel (/admin/load-docs).
const LoadDocsRoute = ({ children }) => {
  const { isAuthenticated, canLoadDocs, loading } = useAuth();

  if (loading) return <Cargando />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canLoadDocs) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Admin Route component — deja pasar a super admins y a usuarios con alcance
const AdminRoute = ({ children }) => {
  const { isAuthenticated, canAccessAdmin, loading } = useAuth();

  if (loading) return <Cargando />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccessAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Gate por herramienta — bloquea navegación directa a una tool no asignada
const ToolRoute = ({ toolKey, children }) => {
  const { hasAdminTool, isAdmin, adminTools } = useAuth();
  if (!hasAdminTool(toolKey)) {
    return <Navigate to={firstAllowedAdminPath(isAdmin, adminTools)} replace />;
  }
  return children;
};

// Redirección del índice /admin a la primera herramienta permitida
const AdminIndexRedirect = () => {
  const { isAdmin, adminTools } = useAuth();
  return <Navigate to={firstAllowedAdminPath(isAdmin, adminTools)} replace />;
};

// Estado final para una key de herramienta sin ruta. No redirige a propósito:
// es el punto donde se corta cualquier rebote entre "/" y "/admin".
const HerramientaNoDisponible = () => (
  <div className="flex flex-col items-center justify-center py-24 text-center">
    <p className="text-lg font-bold text-white mb-1">Herramienta no disponible</p>
    <p className="text-sm text-gray-500 max-w-md">
      El acceso que tenés asignado ya no corresponde a ninguna herramienta.
      Pedile a un administrador que revise tus permisos.
    </p>
  </div>
);

// Public Route component. Al estar logueado manda a la raíz, que es quien sabe
// resolver el destino (incluido el caso del loader).
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <Cargando />;

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Raíz: pantalla de inicio / resolvedor de panel */}
      <Route path="/" element={<IndexResolver />} />
      {/* Panel de quickactions (antes vivía en "/") */}
      <Route
        path="/diagnostico"
        element={
          <ProtectedRoute>
            <ChatProvider>
              <ChatPage />
            </ChatProvider>
          </ProtectedRoute>
        }
      />
      {/* Herramienta independiente de carga de documentos (sin menú) */}
      <Route
        path="/load-docs"
        element={
          <LoadDocsRoute>
            <LoadDocsPage />
          </LoadDocsRoute>
        }
      />
      {/* Unified Admin Panel */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route index element={<AdminIndexRedirect />} />
        <Route path="panel-diario" element={<ToolRoute toolKey="panel-diario"><PanelDiarioPage /></ToolRoute>} />
        <Route path="consumo" element={<ToolRoute toolKey="consumo"><ConsumoPage /></ToolRoute>} />
        <Route path="yiqi-import" element={<ToolRoute toolKey="yiqi-import"><YiqiImportPage /></ToolRoute>} />
        <Route path="chat-test" element={<ToolRoute toolKey="chat-test"><AdminChatTestPage /></ToolRoute>} />
        <Route path="usuarios" element={<ToolRoute toolKey="usuarios"><AdminUsersPage /></ToolRoute>} />
        <Route path="ordenes-compra" element={<ToolRoute toolKey="ordenes-compra"><OrdenCompraPage /></ToolRoute>} />
        <Route path="aprobacion-cotizaciones" element={<ToolRoute toolKey="aprobacion-cotizaciones"><AprobacionCotizacionesPage /></ToolRoute>} />
        {/* Herramienta unificada de Cobranzas (dos pestañas, un solo permiso) */}
        <Route path="cobranzas" element={<ToolRoute toolKey="cobranzas"><CobranzasPage /></ToolRoute>} />
        {/* Deep-links viejos: antes eran dos herramientas separadas. */}
        <Route path="recibos-cobro" element={<Navigate to="/admin/cobranzas" replace />} />
        <Route path="retenciones-clientes" element={<Navigate to="/admin/cobranzas" replace />} />
        <Route path="load-docs" element={<ToolRoute toolKey="load-docs"><LoadDocsPage embedded /></ToolRoute>} />
        <Route path="facturas-servicios" element={<ToolRoute toolKey="facturas-servicios"><FacturasServiciosPage /></ToolRoute>} />
        <Route path="proformas" element={<ToolRoute toolKey="proformas"><ProformasPage /></ToolRoute>} />
        <Route path="tareas-programadas" element={<ToolRoute toolKey="tareas-programadas"><TareasProgramadasPage /></ToolRoute>} />
        <Route path="conciliacion-arca" element={<ToolRoute toolKey="conciliacion-arca"><ConciliacionArcaPage /></ToolRoute>} />
        {/* Corta el ciclo si un usuario tiene asignada una key que ya no existe
            (p. ej. si no se corrió la migración de Cobranzas): sin esto, la ruta
            caería en el "*" global → "/" → resolvedor → /admin → y de vuelta. */}
        <Route path="*" element={<HerramientaNoDisponible />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
